import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '../shared/Modal';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { reportAdminService } from '../../services/reportAdminService';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { slugify, dedupeCode } from '../../utils/slugCode';

const emptyForm = {
  label: '',
  field_type: 'textarea',
  is_required: false,
  sort_order: 0,
  autofill_me: false,
  description: '',
};

export function ReportSystemFieldsTab({ systemId }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [pendingDeactivateId, setPendingDeactivateId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);

  const loadFields = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    if (!systemId) return;
    try {
      setLoading(true);
      const data = await reportAdminService.getReportSystemFields(systemId);
      if (isCancelled()) return;
      setFields(data);
    } catch (err) {
      if (isCancelled()) return;
      setError(mapSupabaseError(err));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(loadFields, [systemId]);

  const handleCreate = () => {
    setEditingField(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      label: field.label,
      field_type: field.field_type,
      is_required: field.is_required,
      sort_order: field.sort_order,
      autofill_me: field.autofill_me ?? false,
      description: field.description ?? '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const payload = {
        code: derivedCode,
        label: formData.label.trim(),
        field_type: formData.field_type,
        is_required: formData.is_required,
        sort_order: Number(formData.sort_order) || 0,
        autofill_me: formData.field_type === 'name' ? !!formData.autofill_me : false,
        description: formData.description.trim() || null,
      };
      if (editingField) {
        await reportAdminService.updateReportField(editingField.id, payload);
        toast.success('Report field updated');
      } else {
        await reportAdminService.createReportField({ ...payload, report_system_id: systemId, is_active: true });
        toast.success('Report field created');
      }
      setShowForm(false);
      setEditingField(null);
      await loadFields();
    } catch (err) {
      setError(mapSupabaseError(err));
    }
  };

  const handleDeactivate = (fieldId) => {
    setPendingDeactivateId(fieldId);
  };

  const confirmDeactivate = async () => {
    const fieldId = pendingDeactivateId;
    setPendingDeactivateId(null);
    if (!fieldId) return;
    try {
      setError(null);
      await reportAdminService.deactivateReportField(fieldId);
      toast.success('Report field deactivated');
      await loadFields();
    } catch (err) {
      setError(mapSupabaseError(err));
    }
  };

  // Create: derive from the Label and dedupe against existing field codes.
  // Edit: the code is frozen — keep the existing one (never recompute).
  const derivedCode = editingField
    ? editingField.code
    : dedupeCode(slugify(formData.label), fields.map((f) => f.code));

  return (
    <div className="report-fields">
      {error && <div className="error-message" role="alert">{error}</div>}

      <button className="btn-primary" onClick={handleCreate}>
        Create New Field
      </button>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <table className="report-fields-table">
          <thead>
            <tr>
              <th>Label</th>
              <th>Type</th>
              <th>Required</th>
              <th>Sort</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field.id}>
                <td>{field.label}</td>
                <td>{field.field_type}{field.autofill_me ? ' · Me' : ''}</td>
                <td>{field.is_required ? 'Yes' : 'No'}</td>
                <td>{field.sort_order}</td>
                <td className="report-fields-actions">
                  <button className="btn-edit" onClick={() => handleEdit(field)}>
                    Edit
                  </button>
                  <button className="btn-danger" onClick={() => handleDeactivate(field.id)}>
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <Modal onClose={() => { setShowForm(false); setEditingField(null); }}>
          <h3>{editingField ? 'Edit Report Field' : 'Create Report Field'}</h3>
          <form onSubmit={handleSubmit} noValidate>
            <label>
              Label *
              <input
                type="text"
                required
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                maxLength={200}
                aria-describedby={editingField ? undefined : 'report-field-code-preview'}
              />
            </label>
            {editingField ? (
              <p className="report-field-code-readonly">
                Code: <code>{editingField.code}</code>
              </p>
            ) : (
              formData.label && (
                <small id="report-field-code-preview" className="report-field-code-preview">
                  Code: <code>{derivedCode || '(empty — add letters or numbers)'}</code>
                </small>
              )
            )}
            <label>
              Description
              <textarea
                rows={3}
                maxLength={500}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </label>
            <p className="field-help">
              Shown to users under the field label while they fill in the report. Optional.
            </p>
            <label>
              Field Type
              <select
                value={formData.field_type}
                onChange={(e) => setFormData({ ...formData, field_type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="textarea">Textarea</option>
                <option value="dropdown">Dropdown (single choice) — Country list</option>
                <option value="name">Name (text — for a person's name)</option>
              </select>
            </label>
            {formData.field_type === 'dropdown' && (
              <p className="field-help">
                Options come from the central Country list (managed in Supervisor Assignments → Countries).
              </p>
            )}
            {formData.field_type === 'name' && (
              <>
                <label>
                  <input
                    type="checkbox"
                    checked={formData.autofill_me}
                    onChange={(e) => setFormData({ ...formData, autofill_me: e.target.checked })}
                  />
                  Me — pre-fill with the submitter's own name
                </label>
                <p className="field-help">
                  Renders as a text box the submitter can edit. A pick-from-registered-users version is planned.
                </p>
              </>
            )}
            <label>
              <input
                type="checkbox"
                checked={formData.is_required}
                onChange={(e) => setFormData({ ...formData, is_required: e.target.checked })}
              />
              Required
            </label>
            <label>
              Sort Order
              <input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
              />
            </label>
            <button
              type="submit"
              className="btn-primary"
              disabled={!editingField && (!formData.label.trim() || !derivedCode)}
            >
              {editingField ? 'Save Field' : 'Create Field'}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setShowForm(false); setEditingField(null); }}
            >
              Cancel
            </button>
          </form>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={pendingDeactivateId !== null}
        title="Deactivate this report field?"
        message="The field will be hidden from active report forms. You can reactivate it later from the database."
        confirmText="Deactivate"
        onConfirm={confirmDeactivate}
        onCancel={() => setPendingDeactivateId(null)}
      />
    </div>
  );
}
